import React from "react";
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { FiFileText, FiLayers, FiUser, FiMenu, FiLogOut } from "react-icons/fi"; // Agregamos FiLogOut para el cierre de sesión
import { GoShieldCheck } from "react-icons/go";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { adminTheme } from "../../../theme/adminTheme";
import BreadcrumbsNav from "../../ui/BreadcrumbsNav";
import { authService } from "../../../apiServices/auth.service";
import { useInactivityTimeout } from "../../../hooks/useInactivityTimeout";
import ConfirmModal from "../../ui/ConfirmModal";

const drawerWidth = 260;

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Route Guard y datos del usuario
  const [user, setUser] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const token = localStorage.getItem("token");

  React.useEffect(() => {
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }

    // Cargar perfil guardado inicialmente para velocidad de renderizado
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        setLoading(false); // ya tenemos datos locales, no bloqueamos la UI
      } catch (e) {
        console.error("Error al parsear el usuario:", e);
      }
    }

    // Validar token y actualizar perfil en segundo plano
    const verifyAndFetchProfile = async () => {
      try {
        const res = await authService.getMe();
        localStorage.setItem("user", JSON.stringify(res.user));
        setUser(res.user);
      } catch (error) {
        console.error("Error al verificar sesión / obtener perfil:", error);
        // Si falla la validación, eliminamos datos locales de sesión y redirigimos
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/admin/login", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    verifyAndFetchProfile();
  }, [token, navigate]);

  const [mobileOpen, setMobileOpen] = React.useState(false);
  
  // Control del menú desplegable de perfil
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = React.useCallback(async () => {
    handleMenuClose();
    try {
      await authService.logout();
    } catch (e) {
      console.warn("Error en la llamada de logout en el backend:", e);
    } finally {
      // Limpieza segura local de la sesión
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("lastActivity");
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  // Cierre de sesión automático tras 20 minutos de inactividad, con aviso de 30 segundos
  const { showWarning, countdown, resetTimeout } = useInactivityTimeout(
    handleLogout,
    20 * 60 * 1000,
    30 * 1000
  );

  // Evitamos renderizar elementos del panel privado si no estamos autenticados
  if (!token) {
    return null;
  }

  // Si está cargando y no tenemos información del usuario todavía, mostramos un spinner
  if (loading && !user) {
    return (
      <ThemeProvider theme={adminTheme}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            bgcolor: "background.default",
            gap: 2,
          }}
        >
          <CircularProgress color="primary" />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Verificando sesión...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }
  const isArticles = location.pathname.startsWith("/admin/articulos") || location.pathname === "/admin";
  const isCategories = location.pathname.startsWith("/admin/categorias");
  const isUsuarios = location.pathname.startsWith("/admin/usuarios");

  let headerTitle = "Blog: Artículos";
  if (isCategories) headerTitle = "Blog: Categorías";
  if (isUsuarios) headerTitle = "Configuración: Usuarios";

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <>
      {/* Mobile Drawer Header */}
      <Toolbar
        sx={{
          minHeight: "64px",
          display: { xs: "flex", md: "none" },
          bgcolor: "primary.main",
          color: "primary.contrastText",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700, letterSpacing: "1px" }}>
          CG ADMIN
        </Typography>
      </Toolbar>
      {/* Desktop Drawer Spacer */}
      <Toolbar sx={{ minHeight: "64px", display: { xs: "none", md: "block" } }} />
      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, overflow: "auto", px: 2, py: 3 }}>
        {/* Indicador de Rol para Móviles */}
        <Box sx={{ display: { xs: "flex", sm: "none" }, mb: 3 }}>
          <Chip
            icon={<GoShieldCheck />}
            sx={{
              color: "text.secondary",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.811rem",
              borderRadius: "20px",
              bgcolor: "action.hover",
              padding: "6px 12px",
              width: "100%",
              justifyContent: "flex-start"
            }}
            label={user?.rol?.nombre || "EDITOR BLOG"}
          />
        </Box>
        <Typography
          variant="overline"
          sx={{
            px: 2,
            color: "text.secondary",
            fontWeight: 700,
            letterSpacing: "1.2px",
            display: "block",
            mb: 1,
          }}
        >
          GESTIÓN BLOG
        </Typography>
        <List sx={{ mb: 1 }}>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => { navigate("/admin/articulos"); setMobileOpen(false); }}
              sx={{
                borderRadius: "8px",
                bgcolor: isArticles ? "primary.main" : "transparent",
                color: isArticles ? "primary.contrastText" : "text.secondary",
                "&:hover": {
                  bgcolor: isArticles ? "primary.dark" : "background.default",
                },
              }}
            >
              <ListItemIcon sx={{ color: "inherit", minWidth: "40px" }}>
                <FiFileText size={20} />
              </ListItemIcon>
              <ListItemText primary="Artículos" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 3 }}>
            <ListItemButton
              onClick={() => { navigate("/admin/categorias"); setMobileOpen(false); }}
              sx={{
                borderRadius: "8px",
                bgcolor: isCategories ? "primary.main" : "transparent",
                color: isCategories ? "primary.contrastText" : "text.secondary",
                "&:hover": {
                  bgcolor: isCategories ? "primary.dark" : "background.default",
                },
              }}
            >
              <ListItemIcon sx={{ color: "inherit", minWidth: "40px" }}>
                <FiLayers size={20} />
              </ListItemIcon>
              <ListItemText primary="Categorías" />
            </ListItemButton>
          </ListItem>
        </List>

        <Divider sx={{ my: 2, mx: 2, borderColor: "divider", opacity: 0.8 }} />

        {/*Configuración usuarios*/}
        <Typography
          variant="overline"
          sx={{
            px: 2,
            color: "text.secondary",
            fontWeight: 700,
            letterSpacing: "1.2px",
            display: "block",
            mb: 1,
            mt: 1, 
          }}
        >
          CONFIGURACIÓN
        </Typography>
        <List>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => { navigate("/admin/usuarios"); setMobileOpen(false); }}
              sx={{
                borderRadius: "8px",
                bgcolor: isUsuarios ? "primary.main" : "transparent",
                color: isUsuarios ? "primary.contrastText" : "text.secondary",
                "&:hover": {
                  bgcolor: isUsuarios ? "primary.dark" : "background.default",
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: "inherit", 
                  minWidth: "40px" 
                }}
              >
              <FiUser size={20} />
              </ListItemIcon>
              <ListItemText primary="Usuarios" />
            </ListItemButton>
          </ListItem>
        </List>

        {/* Botón Cerrar Sesión en Sidebar */}
        <Box sx={{ px: 2, pb: 3, mt: "auto" }}>
          <Button
            variant="text"
            startIcon={<FiLogOut size={16} />}
            fullWidth
            onClick={handleLogout}
            sx={{
              justifyContent: "flex-start",
              borderRadius: "8px",
              padding: "10px 16px",
              textTransform: "none",
              fontWeight: 600,
              color: "error.main",
              "&:hover": {
                bgcolor: "rgba(211, 47, 47, 0.04)",
              },
            }}
          >
            Cerrar sesión
          </Button>
        </Box>
      </Box>
    </>
  );

  return (
    <ThemeProvider theme={adminTheme}>
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default", borderRadius: "0px" }}>
        <CssBaseline />

        <AppBar
          position="fixed"
          sx={{
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${drawerWidth}px` },
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          }}
        >
          <Toolbar disableGutters sx={{ minHeight: "64px", px: { xs: 2, md: 0 } }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: "none" } }}
            >
              <FiMenu />
            </IconButton>

            {/* Logo area - matches drawer width, only visible on desktop */}
            <Box
              sx={{
                width: drawerWidth,
                bgcolor: "primary.main",
                height: "64px",
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                position: "absolute",
                left: `-${drawerWidth}px`,
              }}
            >
              <Typography variant="h6" noWrap component="div" sx={{ color: "primary.contrastText", fontWeight: 700, letterSpacing: "1px" }}>
                CG ADMIN
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexGrow: 1, alignItems: "center", px: 3, justifyContent: "space-between" }}>
              <Typography variant="h6" noWrap component="div" sx={{ color: "primary.main", fontWeight: 600 }}>
                {headerTitle}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  icon={<GoShieldCheck />}
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    color: "text.secondary",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.811rem",
                    borderRadius: "20px",
                    bgcolor: "action.hover",
                    padding: "6px 22px"
                  }}
                  label={user?.rol?.nombre || "EDITOR BLOG"}
                />

                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls={openMenu ? "profile-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={openMenu ? "true" : undefined}
                  onClick={handleMenuClick}
                  color="inherit"
                  sx={{ bgcolor: "action.hover", "&:hover": { bgcolor: "divider" } }}
                >
                  <FiUser size={20} color="inherit" style={{ color: "var(--color-primary, #0069a7)" }} />
                </IconButton>

                <Menu
                  id="profile-menu"
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleMenuClose}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1.5,
                        minWidth: 200,
                        borderRadius: "12px",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                        border: "1px solid",
                        borderColor: "divider",
                      }
                    }
                  }}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
                      {user?.nombre || "Administrador CG"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem", mt: 0.5 }}>
                      {user?.email || "admin@consultoria.com"}
                    </Typography>
                  </Box>
                  <Divider />
                  <MenuItem onClick={handleLogout} sx={{ color: "error.main", py: 1, px: 2, gap: 1.5 }}>
                    <FiLogOut size={16} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Cerrar sesión
                    </Typography>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { display: "flex", flexDirection: "column", boxSizing: "border-box", width: drawerWidth, borderColor: "divider", bgcolor: "background.paper" },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              display: "flex",
              flexDirection: "column",
              width: drawerWidth,
              boxSizing: "border-box",
              borderColor: "divider",
              bgcolor: "background.paper",
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 4 }, pt: { xs: 12, md: 12 }, width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` } }}>
          <BreadcrumbsNav />
          <Outlet />
        </Box>
      </Box>
      <ConfirmModal
        open={showWarning}
        title="Tu sesión está por expirar"
        content={`Tu sesión se cerrará automáticamente por inactividad en ${countdown} segundos. ¿Deseas seguir navegando?`}
        confirmText="Seguir navegando"
        cancelText="Cerrar sesión"
        onConfirm={resetTimeout}
        onCancel={handleLogout}
      />
    </ThemeProvider>
  );
}